<template>
  <div class="workflows-page">
    <!-- Header -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">
          <span class="gradient-text">{{ $t('workflows_page.title') }}</span>
        </h1>
        <p class="page-subtitle">{{ $t('workflows_page.subtitle') }}</p>

        <!-- Search / Filter -->
        <div class="filter-bar">
          <div class="search-box">
            <span class="search-icon">🔍</span>
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('workflows_page.search_placeholder')"
              class="search-input"
            />
          </div>

          <!-- Language Tabs -->
          <div class="lang-tabs">
            <button
              class="lang-tab"
              :class="{ active: selectedLang === '' }"
              @click="selectedLang = ''"
            >All</button>
            <button
              class="lang-tab"
              :class="{ active: selectedLang === 'ko' }"
              @click="selectedLang = 'ko'"
            >🇰🇷 한국어</button>
            <button
              class="lang-tab"
              :class="{ active: selectedLang === 'en' }"
              @click="selectedLang = 'en'"
            >🇺🇸 English</button>
          </div>



          <!-- Category Tabs -->
          <div class="category-tabs">
            <button
              class="cat-tab"
              :class="{ active: selectedCategory === '' }"
              @click="selectedCategory = ''"
            >
              {{ $t('workflows_page.filter_all') }}
            </button>
            <button
              class="cat-tab"
              v-for="cat in categories"
              :key="cat"
              :class="{ active: selectedCategory === cat }"
              @click="selectedCategory = cat"
            >
              {{ cat }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Grid -->
    <div class="container">
      <div class="workflow-count" v-if="filteredWorkflows.length">
        {{ $t('workflows_page.count', { n: filteredWorkflows.length }) }}
      </div>

      <div class="workflows-grid" v-if="visibleWorkflows.length">
        <WorkflowCard
          v-for="(wf, idx) in visibleWorkflows"
          :key="wf.id"
          :workflow="wf"
          :owned="ownedNames.includes(wf.name)"
          class="animate-in"
          :style="{ animationDelay: (idx % 30) * 40 + 'ms' }"
          @select="openModal"
        />
      </div>

      <!-- Load More Sentinel -->
      <div
        v-if="hasMore"
        ref="sentinel"
        class="load-more-sentinel"
      >
        <div class="loading-spinner"></div>
      </div>

      <div class="empty-state" v-if="!visibleWorkflows.length && !loading">
        <div class="empty-icon">🔍</div>
        <p>{{ $t('workflows_page.empty_title') }}</p>
        <button class="btn-reset" @click="searchQuery = ''; selectedCategory = ''; selectedLang = ''">
          {{ $t('workflows_page.empty_reset') }}
        </button>
      </div>
    </div>

    <!-- Modal -->
    <WorkflowModal
      :visible="showModal"
      :workflow="selectedWorkflow"
      :owned="ownedNames.includes(selectedWorkflow.name)"
      @close="closeModal"
      @downloaded="onDownloaded"
    />
  </div>
</template>

<script>
import WorkflowCard from '../components/WorkflowCard.vue'
import WorkflowModal from '../components/WorkflowModal.vue'
import { auth } from '../auth'

const API_BASE = window.location.origin
const PAGE_SIZE = 30

export default {
  name: 'WorkflowsView',
  components: {
    WorkflowCard,
    WorkflowModal,
  },
  data() {
    return {
      workflows: [],
      categories: [],
      searchQuery: '',
      selectedCategory: '',
      selectedLang: '',
      showModal: false,
      selectedWorkflow: {},
      loading: true,
      ownedNames: [],
      displayCount: PAGE_SIZE,
      observer: null,
    }
  },
  computed: {
    filteredWorkflows() {
      let items = this.workflows
      if (this.selectedLang) {
        // 단일 언어(ko/en) 워크플로우는 선택 언어와 다르면 제외
        // multi 워크플로우는 항상 표시 (API에서 이미 해당 언어로 resolve됨)
        items = items.filter(w => {
          const wLang = w.lang || 'en'
          return wLang === this.selectedLang || wLang === 'multi'
        })
      }
      if (this.selectedCategory) {
        items = items.filter(w => w.category === this.selectedCategory)
      }
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase()
        items = items.filter(
          w =>
            w.name.toLowerCase().includes(q) ||
            w.description.toLowerCase().includes(q)
        )
      }
      return items
    },
    visibleWorkflows() {
      return this.filteredWorkflows.slice(0, this.displayCount)
    },
    hasMore() {
      return this.displayCount < this.filteredWorkflows.length
    },
  },
  watch: {
    // Reset display count when filters change
    searchQuery() { this.displayCount = PAGE_SIZE },
    selectedCategory() { this.displayCount = PAGE_SIZE },
    selectedLang() {
      this.displayCount = PAGE_SIZE
      // 언어 탭 변경 시 해당 언어로 API 재호출 (이름/설명 번역 반영)
      this.fetchWorkflows()
    },
    // Re-observe sentinel when hasMore changes
    hasMore(val) {
      this.$nextTick(() => {
        if (val) this.observeSentinel()
        else this.unobserveSentinel()
      })
    },
  },
  async mounted() {
    await this.fetchWorkflows()
    await this.fetchOwned()
    this.$nextTick(() => this.observeSentinel())
  },
  beforeUnmount() {
    this.unobserveSentinel()
  },
  methods: {
    observeSentinel() {
      if (this.observer) this.observer.disconnect()
      const el = this.$refs.sentinel
      if (!el) return
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.displayCount += PAGE_SIZE
        }
      }, { rootMargin: '200px' })
      this.observer.observe(el)
    },
    unobserveSentinel() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },
    async fetchWorkflows() {
      try {
        const lang = this.selectedLang || this.$i18n?.locale || 'en'
        const res = await fetch(`${API_BASE}/api/workflows?lang=${lang}`)
        const data = await res.json()
        this.workflows = data.workflows
        this.categories = data.categories
      } catch (err) {
        console.error('Failed to fetch workflows:', err)
      } finally {
        this.loading = false
      }
    },
    async fetchOwned() {
      if (!auth.isLoggedIn()) return
      try {
        const res = await auth.apiFetch('/api/workflows/owned')
        if (res.ok) {
          const data = await res.json()
          this.ownedNames = data.workflows || []
        }
      } catch {
        // ignore
      }
    },
    async openModal(wf) {
      try {
        // Record view
        fetch(`${API_BASE}/api/workflows/${wf.id}/view`, { method: 'POST' })

        const lang = this.$i18n?.locale || 'en'
        const res = await fetch(`${API_BASE}/api/workflows/${wf.id}?lang=${lang}`)
        this.selectedWorkflow = await res.json()
      } catch {
        this.selectedWorkflow = wf
      }

      // Update local view count on the card
      const idx = this.workflows.findIndex(w => w.id === wf.id)
      if (idx !== -1) {
        this.workflows[idx].views = (this.workflows[idx].views || 0) + 1
      }

      this.showModal = true
    },
    closeModal() {
      this.showModal = false
    },
    onDownloaded(workflowId) {
      // Update local download count on the card
      const idx = this.workflows.findIndex(w => w.id === workflowId)
      if (idx !== -1) {
        this.workflows[idx].downloads = (this.workflows[idx].downloads || 0) + 1
      }
    },
  },
}
</script>

<style scoped>
/* ── Page Header ── */
.page-header {
  background: rgba(10, 10, 10, 0.5);
  padding: 40px 0 32px;
  border-bottom: 1px solid #1a1a1a;
}

.page-title {
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 900;
  text-align: center;
  margin-bottom: 10px;
}

.page-subtitle {
  text-align: center;
  color: var(--subtext0);
  font-size: 15px;
  margin-bottom: 32px;
}

/* ── Filter Bar ── */
.filter-bar {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(13, 13, 13, 0.7);
  border: 1px solid #1a1a1a;
  border-radius: var(--radius-sm);
  padding: 10px 18px;
  width: 100%;
  max-width: 480px;
  transition: border-color var(--transition);
}

.search-box:focus-within {
  border-color: #50c878;
}

.search-icon {
  font-size: 16px;
  opacity: 0.6;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--text);
  font-size: 15px;
  font-family: inherit;
}

.search-input::placeholder {
  color: var(--overlay0);
}

/* ── Language Tabs ── */
.lang-tabs {
  display: flex;
  gap: 6px;
  justify-content: center;
}

.lang-tab {
  padding: 6px 16px;
  border-radius: 20px;
  border: 1px solid var(--surface2);
  background: transparent;
  color: var(--subtext0);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition);
  font-family: inherit;
}

.lang-tab:hover {
  color: var(--text);
  border-color: var(--overlay1);
  background: var(--surface0);
}

.lang-tab.active {
  background: rgba(80, 200, 120, 0.12);
  color: #50c878;
  border-color: rgba(80, 200, 120, 0.3);
}

.type-persona-tab.active {
  background: var(--mauve);
  border-color: var(--mauve);
}

.type-task-tab.active {
  background: var(--teal);
  border-color: var(--teal);
}

/* ── Category Tabs ── */
.category-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
}

.cat-tab {
  padding: 7px 16px;
  border-radius: 20px;
  border: 1px solid var(--surface2);
  background: transparent;
  color: var(--subtext0);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition);
  font-family: inherit;
}

.cat-tab:hover {
  color: var(--text);
  border-color: var(--overlay1);
  background: var(--surface0);
}

.cat-tab.active {
  background: rgba(80, 200, 120, 0.12);
  color: #50c878;
  border-color: rgba(80, 200, 120, 0.3);
}

/* ── Grid ── */
.workflow-count {
  font-size: 13px;
  color: var(--subtext0);
  margin: 28px 0 16px;
}

.workflows-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  padding-bottom: 20px;
}

/* ── Load More Sentinel ── */
.load-more-sentinel {
  display: flex;
  justify-content: center;
  padding: 40px 0 60px;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--surface2);
  border-top: 3px solid #50c878;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── Empty State ── */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: var(--subtext0);
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.btn-reset {
  margin-top: 16px;
  padding: 8px 20px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--surface2);
  background: transparent;
  color: var(--subtext1);
  font-size: 14px;
  cursor: pointer;
  transition: all var(--transition);
  font-family: inherit;
}

.btn-reset:hover {
  background: var(--surface0);
  color: var(--text);
}

/* ── Responsive ── */
@media (max-width: 1024px) {
  .workflows-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .workflows-grid {
    grid-template-columns: 1fr;
  }

  .category-tabs {
    gap: 4px;
  }

  .cat-tab {
    padding: 6px 12px;
    font-size: 12px;
  }
}

/* ── Staggered animation ── */
.animate-in {
  opacity: 0;
  animation: fadeSlideIn 0.4s ease forwards;
}

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
